import { PortfolioNav, ThemeSwitcher, UtilityRoutePageShell, buildPortfolioProducts } from '@saas-pages/shared/public/app-shell'
import { Seo } from '@saas-pages/shared/public/seo'
import {
  buildUtilityNavItems,
} from '@saas-pages/shared/public/registry'
import branding from '../../branding.config'
import { AverageMedianCalculatorPage } from '@/pages/average-median-calculator/AverageMedianCalculatorPage'
import { CoinFlipperPage } from '@/pages/coin-flipper/CoinFlipperPage'
import { CombinatoricsCalculatorPage } from '@/pages/combinatorics-calculator/CombinatoricsCalculatorPage'
import { DiceRollerPage } from '@/pages/dice-roller/DiceRollerPage'
import { DiscountCalculatorPage } from '@/pages/discount-calculator/DiscountCalculatorPage'
import { FractionCalculatorPage } from '@/pages/fraction-calculator/FractionCalculatorPage'
import { MarginMarkupCalculatorPage } from '@/pages/margin-markup-calculator/MarginMarkupCalculatorPage'
import { BlogIndexPage } from '@/pages/blog/BlogIndexPage'
import { BlogPostPage } from '@/pages/blog/BlogPostPage'
import { IndexPage } from '@/pages/index/IndexPage'
import { PercentageCalculatorPage } from '@/pages/percentage-calculator/PercentageCalculatorPage'
import { RandomNumberGeneratorPage } from '@/pages/random-number-generator/RandomNumberGeneratorPage'
import { RatioProportionCalculatorPage } from '@/pages/ratio-proportion-calculator/RatioProportionCalculatorPage'
import { UnitConverterPage } from '@/pages/unit-converter/UnitConverterPage'
import { VatCalculatorPage } from '@/pages/vat-calculator/VatCalculatorPage'
import { seoFor } from '@/seo/routes'
import {
  averageMedianCalculatorRoute,
  calcdeckUtilityRegistry,
  coinFlipperRoute,
  combinatoricsCalculatorRoute,
  diceRollerRoute,
  discountCalculatorRoute,
  fractionCalculatorRoute,
  marginMarkupCalculatorRoute,
  percentageCalculatorRoute,
  randomNumberGeneratorRoute,
  ratioProportionCalculatorRoute,
  vatCalculatorRoute,
  type RouteKey,
} from '@/shared/utility-registry'
import { type UnitConverterRouteKey } from '@/shared/unit-converter-routes'
import { resolveCalcdeckSourceToolKey } from './sourceAttribution'

const routePresentation: Partial<Record<RouteKey, { icon: string; useCases: string[] }>> = {
  'percentage-calculator': {
    icon: '%',
    useCases: ['Процент от числа', 'Доля в процентах', 'Увеличить или уменьшить'],
  },
  'average-median-calculator': {
    icon: 'Σ',
    useCases: ['Среднее арифметическое', 'Медиана', 'Сумма и размах'],
  },
  'fraction-calculator': {
    icon: '⅓',
    useCases: ['Действия с дробями', 'Сокращение', 'Смешанные числа'],
  },
  'combinatorics-calculator': {
    icon: 'C',
    useCases: ['Порядок и повторения', 'Точные сочетания', 'Точные размещения'],
  },
  'vat-calculator': {
    icon: 'НДС',
    useCases: ['Начислить НДС', 'Выделить НДС', 'Сумма прописью'],
  },
  'unit-converter': {
    icon: '↔',
    useCases: ['Шесть категорий', 'Автоматический пересчёт', 'Копирование результата'],
  },
  'ratio-proportion-calculator': {
    icon: '∶',
    useCases: ['Решить пропорцию', 'Сократить отношение', 'Масштабировать отношение'],
  },
  'margin-markup-calculator': {
    icon: '₽',
    useCases: ['Прибыль по двум значениям', 'Маржа и наценка', 'Формулы и сравнение'],
  },
  'discount-calculator': {
    icon: '−%',
    useCases: ['Цена после скидки', 'До трёх скидок', 'Обратный расчёт'],
  },
  'random-number-generator': {
    icon: '#',
    useCases: ['Целые и десятичные числа', 'Выборка без повторов', 'Копирование списка'],
  },
  'dice-roller': {
    icon: '⚄',
    useCases: ['d4–d100', 'Формулы 3d6 и 3d6+2', 'Отдельные броски и итог'],
  },
  'coin-flipper': {
    icon: '○',
    useCases: ['Орёл или решка', 'Серия до 100 бросков', 'Последовательность и счёт'],
  },
}

function presentationFor(routeKey: RouteKey) {
  return routePresentation[routeKey] ?? {
    icon: '↔',
    useCases: ['Точная формула пары', 'Автоматический пересчёт', 'Обратный перевод'],
  }
}

type CurrentRoute =
  | (typeof calcdeckUtilityRegistry)[number]
  | { routeKey: 'index'; path: '/' }
  | { routeKey: 'blog'; path: '/blog' }
  | { routeKey: 'blogPost'; path: string; slug: string }

function currentRoutePath(staticPathname?: string): string {
  if (!staticPathname && typeof window === 'undefined') return '/'

  return (staticPathname ?? window.location.pathname).replace(/\/+$/, '') || '/'
}

function resolveRoute(pathname?: string): CurrentRoute {
  const path = currentRoutePath(pathname)
  if (path === '/') return { routeKey: 'index', path: '/' }
  if (path === '/blog') return { routeKey: 'blog', path: '/blog' }
  if (path.startsWith('/blog/')) {
    return {
      routeKey: 'blogPost',
      path,
      slug: decodeURIComponent(path.slice('/blog/'.length)),
    }
  }

  return calcdeckUtilityRegistry.find((route) => route.path === path) ?? percentageCalculatorRoute
}

function renderRoutePage(routeKey: RouteKey) {
  if (routeKey === coinFlipperRoute.routeKey) return <CoinFlipperPage />
  if (routeKey === diceRollerRoute.routeKey) return <DiceRollerPage />
  if (routeKey === averageMedianCalculatorRoute.routeKey) return <AverageMedianCalculatorPage />
  if (routeKey === fractionCalculatorRoute.routeKey) return <FractionCalculatorPage />
  if (routeKey === combinatoricsCalculatorRoute.routeKey) return <CombinatoricsCalculatorPage />
  if (routeKey === ratioProportionCalculatorRoute.routeKey) return <RatioProportionCalculatorPage />
  if (routeKey === vatCalculatorRoute.routeKey) return <VatCalculatorPage />
  if (routeKey === marginMarkupCalculatorRoute.routeKey) return <MarginMarkupCalculatorPage />
  if (routeKey === discountCalculatorRoute.routeKey) return <DiscountCalculatorPage />
  if (routeKey === randomNumberGeneratorRoute.routeKey) return <RandomNumberGeneratorPage />
  if (routeKey.startsWith('unit-converter')) return <UnitConverterPage routeKey={routeKey as UnitConverterRouteKey} />

  return <PercentageCalculatorPage />
}

interface AppProps {
  pathname?: string
}

export function App({ pathname }: AppProps = {}) {
  const requestedPath = currentRoutePath(pathname)
  const route = resolveRoute(requestedPath)
  const isIndex = route.routeKey === 'index'
  const isBlogIndex = route.routeKey === 'blog'
  const isBlogPost = route.routeKey === 'blogPost'
  const seo = seoFor(isBlogPost ? 'blog' : route.routeKey)
  const activeToolRouteKey =
    route.routeKey === 'index' ||
    route.routeKey === 'blog' ||
    route.routeKey === 'blogPost'
      ? percentageCalculatorRoute.routeKey
      : route.routeKey
  const utilityNavItems = buildUtilityNavItems(calcdeckUtilityRegistry, activeToolRouteKey)
  const navItems = [
    {
      key: 'index',
      label: 'Все калькуляторы',
      href: '/',
      current: isIndex,
    },
    ...(isIndex || isBlogIndex || isBlogPost
      ? utilityNavItems.map((item) => ({ ...item, current: false }))
      : utilityNavItems),
    {
      key: 'blog',
      label: 'Блог',
      href: '/blog',
      current: isBlogIndex || isBlogPost,
    },
  ]
  const presentation = presentationFor(activeToolRouteKey)

  return (
    <div className="min-h-screen bg-background text-foreground">
      {isIndex && <Seo {...seo} ogImage={branding.ogImage} />}

      <PortfolioNav
        brandLabel="calcdeck.ru"
        brandHref="/"
        products={buildPortfolioProducts('calcdeck')}
        items={navItems}
        sourceToolKey={resolveCalcdeckSourceToolKey(requestedPath)}
        actions={<ThemeSwitcher storageKey="calcdeck.theme" />}
      />

      <main className="px-4 py-3 sm:py-10">
        {isBlogIndex ? (
          <BlogIndexPage />
        ) : isBlogPost ? (
          <BlogPostPage slug={route.slug} />
        ) : isIndex ? (
          <IndexPage />
        ) : (
          <UtilityRoutePageShell
            registry={calcdeckUtilityRegistry}
            routeKey={route.routeKey}
            seo={seo}
            domain={branding.domain}
            siteName={branding.domain}
            ogImage={branding.ogImage}
            homeLabel="Калькуляторы"
            scenariosLabel="Сценарии"
            howToLabel="Как пользоваться"
            faqLabel="Вопросы"
            otherToolsLabel="Другие калькуляторы"
            allToolsLabel="Все калькуляторы"
            categoryLabel="Калькулятор"
            categoryClassName="border-blue-300 bg-blue-50 text-blue-950 dark:border-blue-700 dark:bg-blue-950/45 dark:text-blue-100"
            skin={{ relatedIconClassName: 'hidden' }}
            useCases={presentation.useCases}
            icon={presentation.icon}
          >
            {renderRoutePage(route.routeKey)}
          </UtilityRoutePageShell>
        )}
      </main>
    </div>
  )
}
